import { Form, TextFieldControl, useToast } from '@foxtaibox/foxtailbox-frontend-core';
import {
  LoginByEmailMutation,
  LoginByLoginMutation,
  useLoginByEmailMutation,
  useLoginByLoginMutation,
} from '@generated/graphql';
import { Button, Stack } from '@mui/material';
import useHandleLogin from 'pages/onboarding/Login/hooks/useHandleLogin';
import { authFieldSx } from 'pages/onboarding/styles';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface ILoginFormData {
  loginOrEmail: string;
  password: string;
}

interface LoginFormProps {
  setLoginLoading: Dispatch<SetStateAction<boolean>>;
}

const LoginForm = ({ setLoginLoading }: LoginFormProps) => {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const form = useForm<ILoginFormData>();
  const { handleSubmit, setError } = form;
  const [loginByLogin, { loading: loginByLoginLoading }] = useLoginByLoginMutation();
  const [loginByEmail, { loading: loginByEmailLoading }] = useLoginByEmailMutation();
  const loading = loginByLoginLoading || loginByEmailLoading;
  const handleLogin = useHandleLogin();

  const handleError = () => {
    setError('loginOrEmail', { message: t('validation.invalidLoginOrPassword') });
    setError('password', { message: t('validation.invalidLoginOrPassword') });
    showErrorToast(t('validation.invalidLoginOrPassword'));
  };

  const handleLoginSuccess = (
    res?: LoginByEmailMutation['loginByEmail'] | LoginByLoginMutation['loginByLogin'],
  ) => {
    const accessToken = res?.accessToken;
    const refreshToken = res?.refreshToken;

    handleLogin(accessToken, refreshToken);
  };

  const onSubmit = (formData: ILoginFormData) => {
    const loginOrEmail = formData.loginOrEmail.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginOrEmail);

    if (isEmail) {
      loginByEmail({
        variables: {
          email: loginOrEmail,
          password: formData.password,
        },
      })
        .then((res) => handleLoginSuccess(res.data?.loginByEmail))
        .catch(handleError);
    } else {
      loginByLogin({
        variables: {
          login: loginOrEmail,
          password: formData.password,
        },
      })
        .then((res) => handleLoginSuccess(res.data?.loginByLogin))
        .catch(handleError);
    }
  };

  useEffect(() => {
    setLoginLoading(loading);
  }, [loading, setLoginLoading]);

  return (
    <Form form={form}>
      <Stack spacing={3}>
        <Stack spacing={1.25}>
          <TextFieldControl
            name='loginOrEmail'
            label={t('loginPage.loginFieldLabel')}
            placeholder={t('loginPage.loginFieldPlaceholder')}
            rules={{ required: true }}
            disabled={loading}
            autoComplete='username'
            sx={authFieldSx}
          />
        </Stack>

        <Stack spacing={1.25}>
          <TextFieldControl
            name='password'
            label={t('password')}
            rules={{ required: true }}
            type='password'
            placeholder={t('loginPage.passwordFieldPlaceholder')}
            disabled={loading}
            autoComplete='current-password'
            sx={authFieldSx}
          />
        </Stack>

        <Button
          variant='contained'
          size='large'
          fullWidth
          loading={loading}
          onClick={handleSubmit(onSubmit)}>
          {t('login')}
        </Button>
      </Stack>
    </Form>
  );
};

export default LoginForm;
